import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINKS = [
  ["Contact", "/#contact"],
];

export function Wordmark({ className = "" }) {
  return (
    <span className={`flex items-center gap-2 ${className}`}>
      <span className="h-2.5 w-2.5 bg-accent" aria-hidden />
      <span className="font-display text-sm font-extrabold uppercase tracking-[0.14em] [font-stretch:110%]">
        Lennon Global
      </span>
    </span>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (href === "/about") {
      navigate("/about");
      return;
    }
    const hash = href.slice(1);
    if (pathname !== "/") {
      navigate("/" + hash);
    } else if (lenis) {
      lenis.scrollTo(hash, { offset: -64, duration: 1.4 });
    }
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-ink/10 bg-paper/90 backdrop-blur-md" data-testid="main-nav">
        <div className="mx-auto flex h-16 max-w-[1500px] items-center justify-between px-6 md:px-10 lg:px-14">
          <a href="/" onClick={(e) => { e.preventDefault(); if (pathname !== "/") navigate("/"); else lenis?.scrollTo(0); }} data-testid="nav-logo" className="text-ink">
            <Wordmark />
          </a>
          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map(([label, href]) => (
              <a
                key={href}
                href={href}
                onClick={(e) => go(e, href)}
                data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative text-[13px] font-semibold uppercase tracking-[0.12em] text-ink/60 transition-colors duration-300 hover:text-ink"
              >
                {label}
                <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-accent transition-[width] duration-300 group-hover:w-full" />
              </a>
            ))}
            <a
              href="https://www.lennonmgmt.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-lennonmgmt-link"
              className="group flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-dim transition-colors duration-300 hover:text-accent"
            >
              Lennon Management
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </nav>
          <button
            className="text-ink lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open-button"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-[80] flex flex-col bg-ink text-paper"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between border-b border-paper/10 px-6">
              <Wordmark />
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close-button" aria-label="Close menu">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {[["Home", "/#home"], ...LINKS].map(([label, href], i) => (
                <motion.a
                  key={href}
                  href={href}
                  onClick={(e) => go(e, href)}
                  data-testid={`mobile-nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.07 * i + 0.1, duration: 0.6, ease: EASE }}
                  className="border-b border-paper/10 py-5 font-display text-4xl font-extrabold uppercase tracking-tight [font-stretch:115%]"
                >
                  {label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
